import { Box } from "@mui/material";
import { useCallback, useRef } from "react";
import { createFactoryRenderer, type RendererHandle } from "@/features/factory-map/renderer";
import { getSimState, useSimulationStore } from "@/features/simulation/public";
import { MAP_COLS, MAP_ROWS, tileToScreen } from "@/lib/pixi/isoCoords";
import { PixiStage, type PixiStageHandle } from "@/lib/pixi/PixiStage";
import { createTicker } from "@/lib/pixi/ticker";

/**
 * 工場マップの React コンポーネント。
 * PixiStage を mount して、その handle に対してレンダラとティッカーを結線する。
 * layoutVersion をキーにして、レイアウト変更時には Pixi 全体を再マウントする。
 */
export function FactoryMap() {
  const rendererRef = useRef<RendererHandle | null>(null);
  const layoutVersion = useSimulationStore((s) => s.layoutVersion);

  const onReady = useCallback((handle: PixiStageHandle) => {
    const { app, layers, camera } = handle;

    // レンダラ初期化（現在の state を渡す）
    const renderer = createFactoryRenderer(layers, getSimState());
    rendererRef.current = renderer;

    // カメラをマップの中心に合わせる
    const center = tileToScreen(MAP_COLS / 2, MAP_ROWS / 2);
    camera.centerOn(center.x, center.y);

    const ticker = createTicker(app, (dt) => {
      useSimulationStore.getState().advance(dt);
      renderer.update(getSimState(), dt * 1000);
    });
    ticker.setRunning(true);
    ticker.start();

    return () => {
      ticker.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <Box sx={{ position: "absolute", inset: 0, background: "#f4efe6" }}>
      <PixiStage key={layoutVersion} onReady={onReady} initialZoom={2.5} />
    </Box>
  );
}
